/**
 * 365Canvas cart: the cart page (<c365-cart>, sections/c365-cart.liquid) and the cart drawer every other page
 * has (<c365-cart-drawer>, sections/c365-cart-drawer.liquid). Both are the same lines with the same behaviour:
 *
 *   quantity / remove / promo code  → Cart AJAX API, then the section is re-rendered (Section Rendering API)
 *   a personalized line             → its thumbnail is the customer's design drawn as a 3/4 mockup in the
 *                                     line's own finish; "Preview" opens the design as saved, flat
 *   "Edit design"                   → asks first, then opens the Personalize tool on that design. The line
 *                                     stays as it is until the new design is saved; then it gets the new
 *                                     design and shows "Updated" for a few seconds. The tool is a full-screen
 *                                     frame over the page, so the customer is back where they were — the open
 *                                     drawer on whatever page, or the cart page.
 *
 * What a line needs for all that is on its element as data-* (snippets/c365-cart-line-data.liquid).
 */
import { CartLinesUpdateEvent } from '@shopify/events';
import { THEME_ROOT, designProperties, loadDesign, markWork, putWork, readDesigns, takeWork, tool, toolProduct } from '@c365/tool';

// Liquid's t filter HTML-escapes ' " & < >; the strings here go to textContent / template literals, so undo it
const D = (s) => (typeof s === 'string' ? s.replace(/&(#39|#34|quot|amp|lt|gt);/g, (m, e) => ({ '#39': "'", '#34': '"', quot: '"', amp: '&', lt: '<', gt: '>' })[e]) : s);
// shopper-facing text: locales c365.js.cart.* (window.C365_T, filled by the section that loads this script); English fallback
const T = (k, f) => D(window.C365_T && window.C365_T[k]) || f;
const UPDATED_MS = 6000; // how long a line says "Updated"
const CHECK = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"></path></svg>';
const PEN = '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 20h9"></path><path d="M16.38 3.62a1 1 0 0 1 3 3L7.37 18.64a2 2 0 0 1-.86.5l-2.87.84a.5.5 0 0 1-.62-.62l.84-2.87a2 2 0 0 1 .5-.86z"></path></svg>';

function addBusinessDays(n) {
  const d = new Date();
  let c = 0;
  while (c < n) {
    d.setDate(d.getDate() + 1);
    const w = d.getDay();
    if (w !== 0 && w !== 6) c++;
  }
  return d.toLocaleDateString(document.documentElement.lang || 'en-US', { month: 'short', day: 'numeric' });
}

/** "Edit this design?" — resolves to whether the customer goes ahead. */
function confirmEdit() {
  return new Promise((resolve) => {
    const box = document.createElement('div');
    box.className = 'c365 c365-confirm';
    box.innerHTML = `
      <div class="c365-confirm__card" role="alertdialog" aria-modal="true" aria-labelledby="c365-confirm-title">
        <span class="c365-confirm__icon">${PEN}</span>
        <b id="c365-confirm-title" class="c365-confirm__title">${T('cart.confirm_title', 'Edit this design?')}</b>
        <p>${T('cart.confirm_text', "You'll go back to the personalization tool. Your cart item stays as it is until you save your changes.")}</p>
        <div class="c365-confirm__actions">
          <button type="button" class="c365-btn c365-btn-secondary" data-answer="no">${T('cart.cancel', 'Cancel')}</button>
          <button type="button" class="c365-btn c365-btn-primary" data-answer="yes">${T('cart.edit_design', 'Edit design')}</button>
        </div>
      </div>`;
    const answer = (yes) => {
      document.removeEventListener('keydown', onKey, true);
      box.remove();
      resolve(yes);
    };
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      answer(false);
    };
    box.addEventListener('click', (e) => {
      const button = e.target.closest('[data-answer]');
      if (button) answer(button.dataset.answer === 'yes');
      else if (e.target === box) answer(false);
    });
    document.addEventListener('keydown', onKey, true);
    document.body.append(box);
    box.querySelector('[data-answer="yes"]').focus();
  });
}

class C365Cart extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (e) => this.onClick(e));
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && e.target.matches('[data-promo-input]')) {
        e.preventDefault();
        this.applyCode();
      }
    });
    const box = this.querySelector('[data-lightbox]');
    box.addEventListener('click', () => (box.hidden = true));
    document.addEventListener('keydown', (e) => e.key === 'Escape' && (box.hidden = true));
    this.drawn = new Map(); // design + finish → mockup picture, kept across re-renders of the section
    this.pictures = new Map(); // preview URL → the picture taken from the canvas when that design was saved here
    this.edits = new Map(); // design id of a line → {picture}: its new design is still uploading
    this.updated = new Map(); // design id → timer: just updated
    this.hydrate();
  }

  get lines() {
    return Array.from(this.querySelectorAll('[data-line]'));
  }

  hydrate() {
    const eta = this.querySelector('[data-eta]');
    if (eta) eta.textContent = `${addBusinessDays(Number(eta.dataset.min))} – ${addBusinessDays(Number(eta.dataset.max))}`;
    // Paid Economy (shop c365.shipping): USD price → the shopper's currency, as checkout converts it
    const ship = this.querySelector('[data-ship-usd]');
    const currency = window.Shopify?.currency;
    if (ship && currency?.active) {
      const amount = Number(ship.dataset.shipUsd) * (Number(currency.rate) || 1);
      try {
        ship.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: currency.active }).format(amount);
      } catch {
        /* keep "Calculated at checkout" */
      }
    }
    this.markDesigns();
    this.drawDesigns();
  }

  /** The picture to show for a line's design: the one from the canvas if it was made here, else its preview. */
  picture(line) {
    return this.edits.get(line.dataset.designId)?.picture || this.pictures.get(line.dataset.design) || line.dataset.design;
  }

  /**
   * Lines whose design is being replaced say "Saving…" and show the new picture; a line that has just been
   * given its new design says "Updated". Checkout waits while a design is still on its way.
   */
  markDesigns() {
    this.lines.forEach((line) => {
      const pill = line.querySelector('[data-design-pill]');
      if (!pill) return;
      const saving = this.edits.has(line.dataset.designId);
      const updated = this.updated.has(line.dataset.designId);
      pill.hidden = !saving && !updated;
      pill.classList.toggle('is-saving', saving);
      pill.innerHTML = saving ? T('cart.saving', 'Saving…') : `${CHECK}${T('cart.updated', 'Updated')}`;
      line.querySelectorAll('[data-edit-design]').forEach((el) => (el.hidden = saving));
      line.querySelectorAll('[data-flat]').forEach((el) => (el.dataset.zoom = this.picture(line)));
    });
    const busy = this.edits.size > 0;
    this.querySelectorAll('[data-checkout]').forEach((button) => {
      button.disabled = busy;
      const label = button.querySelector('[data-checkout-label]');
      if (!label) return;
      label.dataset.text ||= label.textContent;
      label.textContent = busy ? T('cart.saving_design', 'Saving your design…') : label.dataset.text;
    });
  }

  /**
   * Personalized lines show the customer's design the way the product page does: as a 3/4 mockup in the
   * line's own finish (Canvas Print, or its frame colour), drawn by assets/c365-mockup.js. It is the line's
   * thumbnail, and what a click on it opens large. If it cannot be drawn the flat design is shown.
   * ("Preview" under it always opens the flat design as it was saved.)
   * Framed Print lines are drawn at their size with their Mat and Frame, also without a design: from the
   * design's flat front image (data-print-art).
   */
  drawDesigns() {
    const lines = Array.from(this.querySelectorAll('[data-design], [data-print-art]'));
    if (!lines.length) return;
    const art = (line) => (line.dataset.design ? this.picture(line) : line.dataset.printArt);
    const key = (line) =>
      line.dataset.printSize
        ? `print|${line.dataset.printSize}|${line.dataset.printMat}|${line.dataset.printFrame}|${art(line)}`
        : `${line.dataset.designType}|${line.dataset.designFrame}|${art(line)}`;
    const show = (line, src, fresh) => {
      const thumb = line.querySelector('.c365-cart__thumb');
      if (src) {
        const img = thumb.querySelector('img');
        img.removeAttribute('srcset'); // a stock thumbnail's srcset would win over the drawn picture
        img.src = src;
        thumb.dataset.zoom = src; // "Preview" keeps the design as saved
      }
      thumb.classList.add('is-drawn');
      thumb.classList.toggle('is-new', fresh); // fades in the first time only
    };
    // pictures drawn before go straight back in: the section has just been re-rendered
    const todo = lines.filter((line) => {
      const src = this.drawn.get(key(line));
      if (src !== undefined) show(line, src, false);
      return src === undefined;
    });
    if (!todo.length) return;
    const module = import(this.dataset.mockupJs);
    todo.forEach(async (line) => {
      const k = key(line);
      let src = null; // null = could not be drawn, the flat design stays
      try {
        const { mockup, finishFor, printFinish } = await module;
        const d = line.dataset;
        if (d.printSize) {
          // a saved design is drawn from its print file (found from the preview link), as the Lambda prints it
          src = await mockup(art(line), printFinish(d.printFrame, d.printMat), 1200, d.printSize, d.design || null, d.printTheme || null);
        } else {
          const framed = d.designType.toLowerCase().includes('framed');
          src = await mockup(art(line), finishFor(framed ? d.designFrame || 'black' : ''), 1200);
        }
        // decoded once here, so a thumbnail takes it at once
        const picture = new Image();
        picture.src = src;
        await picture.decode();
      } catch (err) {
        console.warn('[c365] design mockup', err);
        src = null;
      }
      this.drawn.set(k, src);
      if (line.isConnected && key(line) === k) show(line, src, true);
    });
  }

  onClick(e) {
    const qtyBtn = e.target.closest('[data-qty]');
    if (qtyBtn) {
      const line = qtyBtn.closest('[data-line]');
      this.update('/cart/change.js', { line: Number(line.dataset.line), quantity: Number(qtyBtn.dataset.qty) });
      return;
    }
    const edit = e.target.closest('[data-edit-design]');
    if (edit) {
      e.preventDefault();
      this.editDesign(edit.closest('[data-line]'));
      return;
    }
    const zoom = e.target.closest('[data-zoom]');
    if (zoom) {
      e.preventDefault(); // "Preview" is a real link for when this script is not there
      const box = this.querySelector('[data-lightbox]');
      box.querySelector('img').src = zoom.dataset.zoom;
      box.hidden = false;
      return;
    }
    if (e.target.closest('[data-promo-toggle]')) {
      const btn = e.target.closest('[data-promo-toggle]');
      const open = btn.getAttribute('aria-expanded') !== 'true';
      btn.setAttribute('aria-expanded', String(open));
      this.querySelector('[data-promo-body]').hidden = !open;
      if (open) this.querySelector('[data-promo-input]').focus();
      return;
    }
    if (e.target.closest('[data-promo-apply]')) return this.applyCode();
    const rm = e.target.closest('[data-promo-remove]');
    if (rm) this.setCodes(this.currentCodes().filter((c) => c !== rm.dataset.promoRemove));
  }

  /* ---------- edit the design of a line ---------- */

  /**
   * Opens the Personalize tool on a line's design. The tool brings back whatever is in its working copy for
   * that template, so the saved design is put there first (and what was there is put back afterwards, unless
   * the edit was saved and nothing on the product page depends on the old copy).
   */
  async editDesign(line) {
    if (!(await confirmEdit())) return;
    const d = line.dataset;
    const { themeId, designId } = d;
    const [w, h] = (d.size || '').split(/[×x]/).map((v) => v.trim());
    const before = takeWork(themeId);
    tool.open({
      page: d.productUrl,
      themeId,
      globalTool: { productId: Number(d.wooId) || Number(d.productId), themeRoot: THEME_ROOT },
      productData: toolProduct({
        variantId: Number(d.variantId),
        themeId,
        sizeCode: d.sizeCode,
        sizeLabel: w && h ? `${w}″ × ${h}″` : d.size,
        price: Number(d.price) / 100,
        moneyFormat: this.dataset.moneyFormat,
        productUrl: window.location.origin + d.productUrl,
      }),
      before: () => loadDesign(themeId, designId),
      on: {
        // Save Design: the customer is back here at once, the uploads go on behind
        saving: ({ image }) => {
          this.edits.set(designId, { picture: image });
          this.hydrate();
        },
        fail: () => {
          this.edits.delete(designId);
          this.hydrate();
        },
        done: async (result) => {
          // a design in progress or saved on the product page still needs the working copy it had;
          // otherwise the tool keeps the design as edited, and is on it for the next edit
          if (readDesigns()[themeId]) putWork(before);
          else markWork(themeId, result.designId);
          await this.replaceDesign(designId, result);
        },
        close: (error) => {
          putWork(before);
          if (error) this.toast(T('cart.open_error', 'We couldn’t open this design. Please try again.'), true);
        },
      },
    });
  }

  /** The new design is saved: the line that carried the old one gets it, with everything else as it was. */
  async replaceDesign(oldId, { previewUrl, designId }) {
    const edit = this.edits.get(oldId);
    try {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const item = cart.items.find((i) => i.properties?._design_id === oldId);
      if (item) {
        if (edit) this.pictures.set(previewUrl, edit.picture);
        this.updated.set(designId, setTimeout(() => this.clearUpdated(designId), UPDATED_MS));
        this.edits.delete(oldId);
        const properties = { ...item.properties, ...designProperties({ id: designId, image: previewUrl }) };
        delete properties.Preview; // older lines carried the preview URL visibly at checkout
        const done = await this.update('/cart/change.js', { id: item.key, quantity: item.quantity, properties });
        if (!done) throw new Error('cart not updated');
        this.toast(T('cart.design_updated', 'Design updated'));
        return;
      }
    } catch (err) {
      console.warn('[c365] cart line not updated', err);
      this.toast(T('cart.update_error', 'Your new design was saved, but the cart could not be updated. Please try again.'), true);
    }
    this.edits.delete(oldId);
    this.clearUpdated(designId);
    this.hydrate();
  }

  clearUpdated(designId) {
    clearTimeout(this.updated.get(designId));
    if (this.updated.delete(designId)) this.markDesigns();
  }

  /** Short message at the top of the page. */
  toast(text, isError = false) {
    document.querySelector('.c365-toast')?.remove();
    const el = document.createElement('div');
    el.className = `c365 c365-toast${isError ? ' c365-toast--error' : ''}`;
    el.setAttribute('role', 'status');
    el.innerHTML = isError ? '' : CHECK;
    el.append(text);
    document.body.append(el);
    setTimeout(() => el.remove(), isError ? 6000 : 2800);
  }

  /* ---------- promo codes ---------- */

  currentCodes() {
    return Array.from(this.querySelectorAll('[data-promo-remove]')).map((b) => b.dataset.promoRemove);
  }

  async applyCode() {
    const input = this.querySelector('[data-promo-input]');
    const code = input.value.trim();
    if (!code) return;
    const cart = await this.setCodes([...this.currentCodes(), code]);
    const ok = cart?.discount_codes?.find((d) => d.code.toLowerCase() === code.toLowerCase())?.applicable;
    const msg = this.querySelector('[data-promo-msg]');
    msg.hidden = false;
    msg.classList.toggle('is-ok', !!ok);
    msg.textContent = ok ? T('cart.code_applied', 'Code applied.') : T('cart.code_invalid', 'This code isn’t valid for your cart.');
    if (!ok) this.setCodes(this.currentCodes().filter((c) => c.toLowerCase() !== code.toLowerCase()), { keepMsg: true });
  }

  setCodes(codes, opts = {}) {
    return this.update('/cart/update.js', { discount: codes.join(',') }, opts);
  }

  /* ---------- cart changes ---------- */

  /** Puts freshly rendered section HTML in place of what is shown. */
  render(html) {
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('[data-cart-root]');
    if (fresh) this.querySelector('[data-cart-root]').replaceWith(fresh);
    // the drawer's item count sits in its header, outside the re-rendered root
    const count = doc.querySelector('[data-drawer-count]');
    if (count) this.querySelector('[data-drawer-count]')?.replaceWith(count);
    this.hydrate();
  }

  async update(url, body, { keepMsg = false } = {}) {
    const layout = this.querySelector('[data-cart-busy]');
    layout?.classList.add('is-busy');
    const deferred = CartLinesUpdateEvent.createPromise();
    if (body.line || body.id) {
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: body.quantity === 0 ? 'remove' : 'update',
          context: 'cart',
          lines: [{ merchandiseId: '', quantity: body.quantity }],
          promise: deferred.promise,
        })
      );
    }
    try {
      const res = await fetch(`${url}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...body, sections: this.dataset.section }),
      });
      const cart = await res.json();
      if (!res.ok) throw new Error(cart.description || cart.message || 'cart error');
      const msg = this.querySelector('[data-promo-msg]');
      const msgState = keepMsg && msg ? { text: msg.textContent, ok: msg.classList.contains('is-ok') } : null;
      this.render(cart.sections?.[this.dataset.section]);
      if (msgState) {
        const m = this.querySelector('[data-promo-msg]');
        if (m) {
          m.hidden = false;
          m.textContent = msgState.text;
          m.classList.toggle('is-ok', msgState.ok);
          const promo = this.querySelector('[data-promo-body]');
          if (promo) promo.hidden = false;
          this.querySelector('[data-promo-toggle]')?.setAttribute('aria-expanded', 'true');
        }
      }
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { itemCount: cart.item_count } });
      return cart;
    } catch (err) {
      deferred.reject?.(err);
      this.querySelector('[data-cart-busy]')?.classList.remove('is-busy');
      return null;
    }
  }
}

/**
 * The cart as a panel from the right, on every page but the cart page. Opens after Add to cart and from the
 * cart icon in the header; `open()` always shows the cart as it is now.
 */
class C365CartDrawer extends C365Cart {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-drawer-close]') || e.target.matches('[data-drawer-backdrop]')) this.close();
    });
    document.addEventListener('keydown', (e) => {
      const lightbox = this.querySelector('[data-lightbox]');
      if (e.key === 'Escape' && !this.hidden && lightbox.hidden && !document.querySelector('.c365-confirm')) this.close();
    });
  }

  get isOpen() {
    return !this.hidden;
  }

  async open() {
    if (this.hidden) {
      this.overflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      this.hidden = false;
      requestAnimationFrame(() => this.classList.add('is-open'));
      this.querySelector('[data-drawer-close]')?.focus();
    }
    await this.refresh();
  }

  close() {
    if (this.hidden) return;
    this.classList.remove('is-open');
    this.hidden = true;
    document.documentElement.style.overflow = this.overflow || '';
  }

  /** The cart as it is now (lines may have been added from the page behind). */
  async refresh() {
    try {
      const root = window.Shopify?.routes?.root || '/';
      const res = await fetch(`${root}?sections=${encodeURIComponent(this.dataset.section)}`);
      const sections = await res.json();
      this.render(sections[this.dataset.section]);
    } catch (err) {
      console.warn('[c365] cart drawer', err);
    }
  }
}

if (!customElements.get('c365-cart')) customElements.define('c365-cart', C365Cart);
if (!customElements.get('c365-cart-drawer')) customElements.define('c365-cart-drawer', C365CartDrawer);
